import React, { useState, useEffect, useRef } from "react"
import Modal from "aldoo-ra/Modal"
import PubSub from "pubsub-js"
import ClassTable from "./components/ClassTable"
import ClassPreview from "./components/ClassPreview"
import PropertyEditor from "./components/PropertyEditor"
import { useCSSClassManager } from "../hooks/useCSSClassManager"
import { useStyleGeneration } from "./hooks/useStyleGeneration"
import { getDefaultProperties } from "./data/property-definitions"
// Removed surgical updates - now handled by centralized CSS manager

const StyleClassEditor = ({ isOpen, onClose, htmlContent = "" }) => {

  const [isPropertyEditorOpen, setIsPropertyEditorOpen] = useState(false)


  const [currentClass, setCurrentClass] = useState(null)
  const [isCreatingNewClass, setIsCreatingNewClass] = useState(false)
  const [originalNonClassCSS, setOriginalNonClassCSS] = useState("")
  const [disabledClasses, setDisabledClasses] = useState(new Set())
  const lastPublishedContentRef = useRef("")

  // Use centralized CSS class manager
  const cssManager = useCSSClassManager()
  const [selectedClasses, setSelectedClasses] = useState([])

  // Selection management functions
  const toggleClassSelection = (classItem) => {
    const isSelected = selectedClasses.some(cls => cls.name === classItem.name)
    if (isSelected) {
      setSelectedClasses(prev => prev.filter(cls => cls.name !== classItem.name))
    } else {
      // Always get the most up-to-date class data from centralized manager
      const upToDateClass = cssManager.getClass(classItem.name) || classItem
      setSelectedClasses(prev => {
        const filtered = prev.filter(cls => cls.name !== classItem.name)
        return [...filtered, upToDateClass]
      })
    }
  }

  // Use style generation hook
  const {
    parseClassesFromHTML,
    extractClassProperties,
    extractStyleContent,
    generateUpdatedHTML,
  } = useStyleGeneration()

  // Initialize classes data when dialog opens - get fresh content from ContentManager
  useEffect(() => {
    if (isOpen) {
      // Get the current content from ContentManager via PubSub request
      let currentContent = htmlContent

      // Request current content from ContentManager
      const getCurrentContent = () => {
        return new Promise((resolve) => {
          const token = PubSub.subscribe(
            "html-editor:current-content-response",
            (msg, data) => {
              PubSub.unsubscribe(token)
              resolve(data.htmlContent || htmlContent)
            }
          )

          // Request current content
          PubSub.publish("html-editor:get-current-content", {
            requestId: Date.now(),
          })

          // Fallback to original content after timeout
          setTimeout(() => {
            PubSub.unsubscribe(token)
            resolve(htmlContent)
          }, 500)
        })
      }

      getCurrentContent().then((freshContent) => {
        if (freshContent) {
          // Parse existing classes from current HTML (not static prop)
          const classNames = parseClassesFromHTML(freshContent)

          // Create class objects with extracted properties
          const parsedClasses = classNames.map((className) => ({
            id: className,
            name: className,
            type: "CSS Class",
            properties: {
              ...getDefaultProperties(),
              ...extractClassProperties(freshContent, className),
            },
          }))

          cssManager.initialize(parsedClasses)

          // Extract non-class CSS for later reconstruction
          const { nonClassCSS } = extractStyleContent(freshContent, classNames)
          setOriginalNonClassCSS(nonClassCSS)
        } else {
          cssManager.initialize([])
          setOriginalNonClassCSS("")
        }
      })
    } else {
      cssManager.initialize([])
      setOriginalNonClassCSS("")
    }
  }, [
    isOpen,
    parseClassesFromHTML,
    extractClassProperties,
    extractStyleContent,
  ])

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setOriginalNonClassCSS("")
      setCurrentClass(null)
      setIsCreatingNewClass(false)
      setIsPropertyEditorOpen(false)
      lastPublishedContentRef.current = ""
    }
  }, [isOpen])

  // Create a stable hash of classesData to prevent infinite loops
  const classesDataHash = useRef("")

  // DISABLED: Auto-update was causing conflicts with explicit surgical updates
  // Now we only send surgical updates when explicitly needed (create, update, delete, rename)
  // This makes local classesData the single source of truth

  // Keep the hash ref for potential future use
  // const classesDataHash = useRef("")

  // Handle editing a class
  const handleEditClass = (classItem) => {
    setCurrentClass(classItem)
    setIsCreatingNewClass(false)
    setIsPropertyEditorOpen(true)
  }

  // Handle adding a new class
  const handleAddClass = () => {
    const newClass = {
      name: "",
      properties: getDefaultProperties(),
    }
    setCurrentClass(newClass)
    setIsCreatingNewClass(true)
    setIsPropertyEditorOpen(true)
  }

  // Handle creating a new class
  const handleClassCreate = (className, properties) => {
    try {
      const createdClass = cssManager.createClass(className, properties)

      // Auto-select new class for preview
      setSelectedClasses((prev) => [...prev, createdClass])

      // Switch to editing the newly created class - keep PropertyEditor open
      setCurrentClass(createdClass)
      setIsCreatingNewClass(false)
      // Keep setIsPropertyEditorOpen(true) - don't close it!
    } catch (error) {
      console.error("Error creating class:", error)
    }
  }

  // Handle updating a class
  const handleClassUpdate = (property, value) => {
    if (!currentClass?.name) return

    try {
      const updatedClass = cssManager.updateProperty(currentClass.name, property, value)

      // Update current class state with the updated class from manager
      setCurrentClass(updatedClass)
    } catch (error) {
      console.error("Error updating class property:", error)
    }
  }

  // Handle class-level updates (like name changes from modifiers)
  const handleClassMetadataUpdate = (updatedClass) => {
    if (!updatedClass?.name) return

    const oldName = currentClass?.name
    const newName = updatedClass.name

    // If name changed, we need to handle class rename
    if (oldName && oldName !== newName) {
      try {
        // Preserve existing properties when renaming the class
        const existingProperties = currentClass?.properties || {}
        const newProperties = { ...existingProperties, ...(updatedClass.properties || {}) }

        // Use centralized rename - handles CSS updates automatically
        const renamedClass = cssManager.renameClass(oldName, newName, newProperties)

        // Update current class state
        setCurrentClass(renamedClass)

        // Update selection to use new class
        setSelectedClasses(prev => {
          const filtered = prev.filter(cls => cls.name !== oldName && cls.name !== newName)
          return [...filtered, renamedClass]
        })
      } catch (error) {
        console.error("Error renaming class:", error)
      }
    } else {
      // Update current class state for non-rename changes
      setCurrentClass(updatedClass)

      if (updatedClass.properties) {
        try {
          cssManager.updateClass(newName, updatedClass.properties)
        } catch (error) {
          console.error("Error updating class properties:", error)
        }
      }
    }
  }

  // Handle toggling disabled state of a class
  const handleToggleClassDisabled = (className) => {
    setDisabledClasses((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(className)) {
        newSet.delete(className)
      } else {
        newSet.add(className)
      }
      return newSet
    })
  }

  // Handle removing class from selection (delete functionality)
  const handleRemoveFromSelection = (classItem) => {
    toggleClassSelection(classItem)
  }

  // Handle deleting a class
  const handleDeleteClass = (className) => {
    try {
      cssManager.deleteClass(className)

      // Remove from selection if it was selected
      setSelectedClasses(prev => prev.filter(cls => cls.name !== className))
    } catch (error) {
      console.error("Error deleting class:", error)
    }
  }

  // Close property editor
  const handleClosePropertyEditor = () => {
    setIsPropertyEditorOpen(false)
    setCurrentClass(null)
    setIsCreatingNewClass(false)
  }

  return (
    <>
      {/* Main StyleClassEditor Modal */}
      <Modal
        isOpen={isOpen}
        onClose={() => {
          // No need for manual update - useEffect handles all updates
          onClose && onClose()
        }}
        variant="admin"
        title="Style Class Editor"
        className="w-full h-full"
        closeOnEscape
        styleOverrides={{
          container: "w-full h-full bg-admin_paper overflow-hidden",
          header: "border-b border-admin_bordered px-6 py-4",
          contentWrapper: "flex-1 overflow-hidden",
          content: "h-full p-0 overflow-hidden",
        }}
      >
        <div className="h-full flex flex-col">
          {/* Main Content */}
          <div className="flex-1 flex overflow-hidden">
            {/* Left Side - Preview Area */}
            <ClassPreview
              selectedClasses={selectedClasses}
              disabledClasses={disabledClasses}
              onEditClass={handleEditClass}
              onRemoveFromSelection={handleRemoveFromSelection}
              onToggleDisabled={handleToggleClassDisabled}
            />

            {/* Right Side - Class Table */}
            <ClassTable
              classesData={cssManager.classes}
              selectedClasses={selectedClasses}
              disabledClasses={disabledClasses}
              onClassSelection={(classItem) => {
                // Toggle selection - add if not selected, remove if already selected
                toggleClassSelection(classItem)
              }}
              onEditClass={handleEditClass}
              onDeleteClass={handleDeleteClass}
              onAddClass={handleAddClass}
            />
          </div>
        </div>
      </Modal>

      {/* Property Editor Modal */}
      <PropertyEditor
        key={currentClass?.name || 'new-class'}
        isOpen={isPropertyEditorOpen}
        onClose={handleClosePropertyEditor}
        currentClass={currentClass}
        onClassUpdate={handleClassUpdate}
        onClassMetadataUpdate={handleClassMetadataUpdate}
        onClassCreate={handleClassCreate}
        isCreatingNew={isCreatingNewClass}
        existingClasses={cssManager.classes}
      />
    </>
  )
}

export default StyleClassEditor
